import React from 'react';
import { FunnelIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useFilterOptions } from '../../hooks/useFilterOptions';

export default function FilterBar({
  organizationId,
  campusId,
  groupId,
  onFilterChange
}) {
  const {
    organizations,
    campuses,
    groups,
    loading,
    loadingCampuses,
    loadingGroups,
    organizationsError,
    campusesError,
    groupsError
  } = useFilterOptions({ organizationId, campusId });

  const handleOrgChange = (e) => {
    onFilterChange({
      organizationId: e.target.value || null,
      campusId: null,
      groupId: null
    });
  };

  const handleCampusChange = (e) => {
    onFilterChange({
      organizationId,
      campusId: e.target.value || null,
      groupId: null
    });
  };

  const handleGroupChange = (e) => {
    onFilterChange({
      organizationId,
      campusId,
      groupId: e.target.value || null
    });
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <FunnelIcon className="h-4 w-4" />
          <span className="font-medium">Filter by:</span>
        </div>

        <div className="flex items-center gap-1">
          <select
            value={organizationId || ''}
            onChange={handleOrgChange}
            disabled={loading}
            className={`rounded-md text-sm focus:border-flock-blue focus:ring-flock-blue disabled:opacity-50 ${
              organizationsError ? 'border-red-300 bg-red-50' : 'border-slate-300'
            }`}
          >
            <option value="">
              {loading ? 'Loading...' : organizationsError ? 'Error loading' : 'All Organizations'}
            </option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
          {organizationsError && (
            <ExclamationCircleIcon
              className="h-4 w-4 text-red-500"
              title="Failed to load organizations. Please refresh the page."
            />
          )}
        </div>

        {organizationId && (
          <div className="flex items-center gap-1">
            <select
              value={campusId || ''}
              onChange={handleCampusChange}
              disabled={loadingCampuses}
              className={`rounded-md text-sm focus:border-flock-blue focus:ring-flock-blue disabled:opacity-50 ${
                campusesError ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
            >
              <option value="">
                {loadingCampuses ? 'Loading...' : campusesError ? 'Error loading' : 'All Campuses'}
              </option>
              {campuses.map(campus => (
                <option key={campus.id} value={campus.id}>{campus.name}</option>
              ))}
            </select>
            {campusesError && (
              <ExclamationCircleIcon
                className="h-4 w-4 text-red-500"
                title="Failed to load campuses. Try selecting a different organization."
              />
            )}
          </div>
        )}

        {campusId && (
          <div className="flex items-center gap-1">
            <select
              value={groupId || ''}
              onChange={handleGroupChange}
              disabled={loadingGroups}
              className={`rounded-md text-sm focus:border-flock-blue focus:ring-flock-blue disabled:opacity-50 ${
                groupsError ? 'border-red-300 bg-red-50' : 'border-slate-300'
              }`}
            >
              <option value="">
                {loadingGroups ? 'Loading...' : groupsError ? 'Error loading' : 'All Groups'}
              </option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.ministryName ? `${group.ministryName} - ${group.name}` : group.name}
                </option>
              ))}
            </select>
            {groupsError && (
              <ExclamationCircleIcon
                className="h-4 w-4 text-red-500"
                title="Failed to load groups. Try selecting a different campus."
              />
            )}
          </div>
        )}

        {(organizationId || campusId || groupId) && (
          <button
            onClick={() => onFilterChange({ organizationId: null, campusId: null, groupId: null })}
            className="text-sm text-slate-500 hover:text-slate-700 underline"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}
